/*! Copyright (c) 2021, XAPPmedia */

export default function insertSorted<T>(array: T[], obj: T, comparer: (left: T, right: T) => number): T[] {
    if (!array.length) {
        array.push(obj);
        return array;
    }

    // Handle single-element array case
    if (array.length === 1) {
        const comp = comparer(obj, array[0]);
        if (comp === 0) {
            if (obj === array[0]) {
                return array; // Same object reference, skip duplicate
            }
            // Same comparison value but different object - insert after
            array.push(obj);
            return array;
        }
        if (comp < 0) {
            array.unshift(obj);
        } else {
            array.push(obj);
        }
        return array;
    }

    let leftIndex = 0;
    let rightIndex = array.length - 1;

    while ((rightIndex - leftIndex) > 0) {
        const left = array[leftIndex];
        const right = array[rightIndex];

        const compLeft = comparer(obj, left);

        // Allow items with equal comparison values (e.g., same timestamp) to be inserted.
        // Only skip if it's the exact same object reference.
        if (compLeft === 0) {
            if (obj === left) {
                return array; // Same object reference, skip duplicate
            }
            // Same comparison value but different object - insert after this position
            array.splice(leftIndex + 1, 0, obj);
            return array;
        }

        if (compLeft < 0) {
            array.splice(leftIndex, 0, obj);
            return array;
        }

        const compRight = comparer(obj, right);

        if (compRight === 0) {
            if (obj === right) {
                return array; // Same object reference, skip duplicate
            }
            // Same comparison value but different object - insert after this position
            array.splice(rightIndex + 1, 0, obj);
            return array;
        }

        if (compRight > 0) {
            array.splice(rightIndex + 1, 0, obj);
            return array;
        }

        if (rightIndex - leftIndex <= 1) {
            break;
        }

        const centerIndex = Math.floor((leftIndex + rightIndex) / 2);
        const center = array[centerIndex];

        const comp = comparer(center, obj);

        // Check for same object reference at center
        if (comp === 0 && obj === center) {
            return array; // Same object reference, skip duplicate
        }

        if (comp <= 0) {
            leftIndex = centerIndex;
            continue;
        }
        if (comp > 0) {
            rightIndex = centerIndex;
            continue;
        }
    }
    array.splice(leftIndex + 1, 0, obj);
    return array;
}